import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/native';
import { BaseText } from '@/shared/components/StyledText';
import { IsDesktopProps } from '@/shared/styles/types';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import { Feather } from '@expo/vector-icons';
import { accent, progressBarBackground, white } from '@/shared/styles/colors';
import Button from '@/shared/components/Button';
import { InstantStylingMatchStatus } from '@/shared/types/generated';
import { Animated, Easing } from 'react-native';
import { MainText } from '../stylist/StylistMatchFound';
import { STYLIST_MATCH_EXPIRATION_SECONDS } from '@/shared/constants/instantCall';

interface Props {
  matchStatus: InstantStylingMatchStatus | null;
  timeRemaining: number; // in seconds
  onPressAcceptOrJoinButton: () => void;
  onTimerExpire: () => void;
}

const AcceptTimerSection = ({
  matchStatus,
  timeRemaining,
  onPressAcceptOrJoinButton,
  onTimerExpire,
}: Props) => {
  const { isDesktop } = useBreakpoint();
  const progressAnim = useRef(new Animated.Value(0)).current;
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const [remainingSeconds, setRemainingSeconds] = useState(timeRemaining);

  const showButton =
    !matchStatus?.stylistAccepted ||
    (matchStatus?.stylistAccepted && matchStatus?.clientAccepted);

  // Animate the progress bar with proper cleanup
  useEffect(() => {
    // Calculate the initial progress based on how much time has already elapsed
    const elapsedTime = STYLIST_MATCH_EXPIRATION_SECONDS - timeRemaining;
    const initialProgress = elapsedTime / STYLIST_MATCH_EXPIRATION_SECONDS;
    progressAnim.setValue(initialProgress);

    const animation = Animated.timing(progressAnim, {
      toValue: 1,
      duration: timeRemaining * 1000,
      easing: Easing.linear,
      useNativeDriver: false,
    });

    animation.start();

    return () => {
      animation.stop();
    };
  }, [progressAnim, timeRemaining]);

  // Tick down the timer every second with proper cleanup
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  const resetTimerAndProgress = useCallback(() => {
    progressAnim.stopAnimation();
    progressAnim.setValue(0);
    setRemainingSeconds(STYLIST_MATCH_EXPIRATION_SECONDS);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [progressAnim]);

  useEffect(() => {
    if (remainingSeconds === 0) {
      resetTimerAndProgress();
      onTimerExpire();
    }
  }, [onTimerExpire, remainingSeconds, resetTimerAndProgress]);

  const formattedTime = `0:${remainingSeconds.toString().padStart(2, '0')}`;

  const handleAcceptPress = () => {
    resetTimerAndProgress();
    onPressAcceptOrJoinButton();
  };

  const AnimatedProgressBar = ({
    progressAnim,
  }: {
    progressAnim: Animated.Value;
  }) => {
    const width = progressAnim.interpolate({
      inputRange: [0, 1],
      outputRange: ['0%', '100%'],
    });

    return (
      <ProgressBarBackground>
        <Animated.View
          style={{ height: 8, borderRadius: 4, backgroundColor: accent, width }}
        />
      </ProgressBarBackground>
    );
  };

  return (
    <OuterContainer isDesktop={isDesktop}>
      <AcceptTimerContainer isDesktop={isDesktop}>
        {!matchStatus?.stylistAccepted && (
          <MainText style={{ marginBottom: isDesktop ? 0 : 24 }}>
            You have <BoldText>{formattedTime} seconds</BoldText> to make a
            decision
          </MainText>
        )}
        {matchStatus?.stylistAccepted && showButton && <PlaceholderView />}
        {showButton ? (
          <Button
            variant="primary"
            text={matchStatus?.clientAccepted ? 'Join Call' : 'Accept Request'}
            onPress={handleAcceptPress}
            size="large"
            leftIcon={
              matchStatus?.clientAccepted ? (
                <Feather
                  name="video"
                  size={20}
                  color={white}
                  style={{ marginRight: 6 }}
                />
              ) : undefined
            }
            customStyle={{ width: isDesktop ? 181 : 320, height: 42 }}
            customLabelStyle={{
              fontSize: 14,
              fontWeight: 'bold',
              letterSpacing: 0.7,
            }}
          />
        ) : (
          <SearchingSpinnerContainer>
            <SearchingSpinnerImage
              source={require('@/assets/images/SearchingSpinner.gif')}
            />
          </SearchingSpinnerContainer>
        )}
      </AcceptTimerContainer>
      <ProgressBarContainer isDesktop={isDesktop}>
        <AnimatedProgressBar progressAnim={progressAnim} />
      </ProgressBarContainer>
    </OuterContainer>
  );
};

const ProgressBarBackground = styled.View({
  width: '100%',
  height: 8,
  backgroundColor: progressBarBackground,
  borderRadius: 4,
  overflow: 'hidden',
});

const BoldText = styled(BaseText)({
  fontSize: 16,
  fontWeight: 'bold',
  letterSpacing: -0.16,
  textAlign: 'center',
});

const AcceptTimerContainer = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  width: '100%',
  flexDirection: isDesktop ? 'row' : 'column',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingHorizontal: 24,
  paddingVertical: 24,
}));

const SearchingSpinnerContainer = styled.View({
  flex: 1,
  width: 42,
  height: 42,
  alignSelf: 'center',
});

const SearchingSpinnerImage = styled.Image({
  width: '100%',
  height: '100%',
  resizeMode: 'contain',
});

const ProgressBarContainer = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  width: '100%',
  marginBottom: isDesktop ? 21 : 0,
  paddingHorizontal: 7,
}));

const PlaceholderView = styled.View({
  width: 50,
});

const OuterContainer = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  flexDirection: isDesktop ? 'column' : 'column-reverse',
}));

export default AcceptTimerSection;
